import type {
  DiagnosisBlock,
  DiagnosisType,
  LearningStep,
  ReadingRecord,
  SelectionAnnotation,
} from "../types";
import { todayYmd, exportBackup } from "../db";

const STATUS_LABEL: Record<string, string> = {
  draft: "草稿",
  diagnosing: "诊断中",
  learning: "学习中",
  done: "已读懂",
};

const BLOCK_LABEL: Record<DiagnosisType, string> = {
  vocab: "词汇",
  phrase: "短语",
  sentence: "句法",
  logic: "逻辑",
  background: "背景",
};

function formatDate(iso: string): string {
  return iso.slice(0, 19).replace("T", " ");
}

function mdBlock(text: string): string {
  return text.replace(/```/g, "\\`\\`\\`");
}

export function buildReadingMarkdown(
  reading: ReadingRecord,
  blocks: DiagnosisBlock[],
  steps: LearningStep[],
  annotations: SelectionAnnotation[],
): string {
  const lines: string[] = [
    `# ${reading.title}`,
    "",
    `- **状态**：${STATUS_LABEL[reading.status] ?? reading.status}`,
    `- **水平**：${reading.level}`,
    `- **创建**：${formatDate(reading.createdAt)}`,
    `- **更新**：${formatDate(reading.updatedAt)}`,
    "",
    "## 原文",
    "",
    mdBlock(reading.text),
    "",
  ];

  if (reading.textTranslation) {
    lines.push("## 译文", "", mdBlock(reading.textTranslation), "");
  }

  if (reading.diagnosisSummary) {
    lines.push("## 诊断摘要", "", mdBlock(reading.diagnosisSummary), "");
  }

  if (blocks.length) {
    lines.push("## 卡点", "");
    for (const block of blocks) {
      lines.push(`### ${BLOCK_LABEL[block.type]}`, "");
      lines.push(`> ${block.excerpt.replace(/\n/g, "\n> ")}`, "");
      if (block.translation) {
        lines.push(`**译文**：${block.translation}`, "");
      }
      if (block.note) {
        lines.push(block.note, "");
      }
    }
  }

  if (steps.length) {
    lines.push("## 学习步骤", "");
    for (const step of steps) {
      const done = step.done ? "✓ 已完成" : "○ 未完成";
      lines.push(`### ${step.stepIndex + 1}. ${step.title}（${done}）`, "");
      lines.push(step.content, "");
      if (step.checkQuestion) {
        lines.push(`**小测**：${step.checkQuestion}`, "");
      }
      if (step.completedAt) {
        lines.push(`*完成于 ${formatDate(step.completedAt)}*`, "");
      }
    }
  }

  if (annotations.length) {
    lines.push("## 划词笔记", "");
    for (const ann of annotations) {
      lines.push(`### 「${ann.selectedText}」`, "", ann.answer, "");
    }
  }

  return lines.join("\n").trim() + "\n";
}

export function buildBatchReportMarkdown(
  readings: ReadingRecord[],
  blocksByReading: Map<string, DiagnosisBlock[]>,
  stepsByReading: Map<string, LearningStep[]>,
  annotationsByReading: Map<string, SelectionAnnotation[]>,
): string {
  const sorted = [...readings].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  const sections = sorted.map((reading) =>
    buildReadingMarkdown(
      reading,
      blocksByReading.get(reading.id) ?? [],
      stepsByReading.get(reading.id) ?? [],
      annotationsByReading.get(reading.id) ?? [],
    ),
  );

  const header = [
    "# ReadThrough 学习报告",
    "",
    `导出时间：${formatDate(new Date().toISOString())}`,
    `共 ${sorted.length} 篇阅读`,
    "",
    "---",
    "",
  ].join("\n");

  return header + sections.join("\n---\n\n");
}

export function downloadMarkdown(filename: string, content: string): void {
  const blob = new Blob([content], { type: "text/markdown;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function batchReportFilename(date = new Date()): string {
  return `readthrough-report-${todayYmd(date)}.md`;
}

export function readingReportFilename(reading: ReadingRecord): string {
  const slug = reading.title
    .replace(/[^\w\u4e00-\u9fff]+/gi, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
  return `readthrough-${slug || reading.id.slice(0, 8)}.md`;
}

export function downloadReadingReport(
  reading: ReadingRecord,
  blocks: DiagnosisBlock[],
  steps: LearningStep[],
  annotations: SelectionAnnotation[],
): void {
  downloadMarkdown(
    readingReportFilename(reading),
    buildReadingMarkdown(reading, blocks, steps, annotations),
  );
}

export async function downloadReadingReportById(readingId: string): Promise<void> {
  const data = await exportBackup();
  const reading = data.readings.find((r) => r.id === readingId);
  if (!reading) {
    throw new Error("阅读记录不存在");
  }
  downloadReadingReport(
    reading,
    data.diagnosisBlocks.filter((b) => b.readingId === readingId),
    data.learningSteps.filter((s) => s.readingId === readingId),
    (data.selectionAnnotations ?? []).filter((a) => a.readingId === readingId),
  );
}
